/**
 * Colour ramps for Ember sprites.
 *
 * A material is a five-step ramp plus the outline drawn where it meets the background. The shader
 * picks a step from lighting; it never invents colours, which is what keeps the output reading as
 * pixel art rather than a render. Skin ramps are sampled from the LPC bodies so Ember bodies sit
 * beside the existing art; everything else is derived from one base colour by `rampFrom`.
 */
export interface Material {
    /** Brightest to darkest. `line` marks creases and limbs crossing the body. */
    light: number
    base: number
    shade: number
    deep: number
    line: number
    outline: number
}

const hex = (s: string): number => parseInt(s.replace("#", ""), 16)

const material = (light: string, base: string, shade: string, deep: string, line: string, outline: string): Material => ({
    light: hex(light),
    base: hex(base),
    shade: hex(shade),
    deep: hex(deep),
    line: hex(line),
    outline: hex(outline),
})

/** Sampled from content/game/media/lpc/spritesheets/body/male/human/<tone>.png. */
export const SKIN: Record<string, Material> = {
    light: material("#fbece6", "#fdd5b7", "#eaa377", "#d28560", "#9e3e37", "#281820"),
    // Dead flesh: a sickly grey-green, the game's zombie "green".
    zombie: material("#d3d8ae", "#aab184", "#848c66", "#636a4b", "#454a33", "#281820"),
    olive: material("#d99f90", "#d98e60", "#a96b4c", "#905e51", "#5a3737", "#281821"),
    brown: material("#c58050", "#a86431", "#7e4f32", "#644334", "#442826", "#120d0f"),
    black: material("#714535", "#61382d", "#3b2924", "#2b2122", "#1c1418", "#070609"),
}

/**
 * Skin for Ember's own (realist) style: picked, not sampled, and set apart from LPC by its shadows,
 * which cool toward violet, and its outline, a deep plum rather than LPC's brown-black.
 */
export const PAINTED_SKIN: Record<string, Material> = {
    light: material("#f8dcc4", "#e9b892", "#c98a6c", "#9c6158", "#6e3d4a", "#25182b"),
    zombie: material("#cdd3a6", "#a6ad80", "#808862", "#5f6648", "#424730", "#25182b"),
    olive: material("#e6bf98", "#c99468", "#9f6c55", "#7a4d4c", "#553243", "#22162a"),
    brown: material("#c98f63", "#a26a45", "#7b4d3c", "#5c3637", "#40242f", "#1a1022"),
    black: material("#8f5f4c", "#6b4337", "#4e2f2d", "#38222a", "#271822", "#110a14"),
}

/** Sampled from the same sheets: glint, sclera, iris, pupil. */
export const EYE = {
    /** The cyan catch-light at the corners of an LPC eye. */
    glint: hex("#50d4ec"),
    /** Ember eyes: a warm catch-light from the fire on a warm near-black. */
    ember: hex("#ffbf5a"),
    warmPupil: hex("#24140f"),
    white: hex("#f2f8f9"),
    iris: hex("#5187b3"),
    pupil: hex("#293d4b"),
}

const rgb = (c: number): [number, number, number] => [(c >> 16) & 255, (c >> 8) & 255, c & 255]
const pack = ([r, g, b]: [number, number, number]): number =>
    (Math.round(Math.max(0, Math.min(255, r))) << 16) |
    (Math.round(Math.max(0, Math.min(255, g))) << 8) |
    Math.round(Math.max(0, Math.min(255, b)))

const toHsl = (c: number): [number, number, number] => {
    const [r, g, b] = rgb(c).map(v => v / 255) as [number, number, number]
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    const l = (max + min) / 2
    if (max === min) return [0, 0, l]
    const d = max - min
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
    return [h * 60, s, l]
}

const fromHsl = (h: number, s: number, l: number): number => {
    const hue = ((h % 360) + 360) % 360
    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
    const m = l - c / 2
    const [r, g, b] =
        hue < 60 ? [c, x, 0] : hue < 120 ? [x, c, 0] : hue < 180 ? [0, c, x] : hue < 240 ? [0, x, c] : hue < 300 ? [x, 0, c] : [c, 0, x]
    return pack([(r + m) * 255, (g + m) * 255, (b + m) * 255])
}

/**
 * A pixel-art ramp from one base colour: shadows drift toward violet and lose lightness, highlights
 * drift toward warm yellow. The same hue shift LPC's cloth uses, so dyed garments read as painted.
 */
export const rampFrom = (base: string | number, outline = "#281820"): Material => {
    const [h, s, l] = toHsl(typeof base === "string" ? hex(base) : base)
    const toward = (target: number, amount: number) => {
        let d = target - h
        if (d > 180) d -= 360
        if (d < -180) d += 360
        return h + d * amount
    }
    return {
        light: fromHsl(toward(55, 0.15), Math.min(1, s * 0.95), Math.min(0.93, l + 0.13)),
        base: fromHsl(h, s, l),
        shade: fromHsl(toward(265, 0.12), Math.min(1, s * 1.02), Math.max(0.05, l - 0.12)),
        deep: fromHsl(toward(265, 0.22), Math.min(1, s * 1.05), Math.max(0.04, l - 0.22)),
        line: fromHsl(toward(265, 0.3), Math.min(1, s * 0.9), Math.max(0.03, l - 0.32)),
        outline: hex(outline),
    }
}

/**
 * Polished metal: the same hue with a near-white glint and a deep shadow, more contrast than cloth
 * or leather, so a breastplate reads as metal at a few pixels.
 */
export const metalFrom = (base: string, outline = "#281820"): Material => {
    const [h, s, l] = toHsl(hex(base))
    return {
        light: fromHsl(h, s * 0.6, Math.min(0.94, l + 0.27)),
        base: fromHsl(h, s, l),
        shade: fromHsl(h, Math.min(1, s * 1.05), Math.max(0.05, l - 0.17)),
        deep: fromHsl(h, Math.min(1, s * 1.1), Math.max(0.04, l - 0.31)),
        line: fromHsl(h, Math.min(1, s * 1.1), Math.max(0.03, l - 0.4)),
        outline: hex(outline),
    }
}

/** Bone: ivory with warm grey shadows -- the shared ramp's purple-ward shift turns pale bone pink. */
export const BONE = material("#f1ebd8", "#d9d0b4", "#aca186", "#7d735f", "#544c3f", "#281820")

/** A skeleton's bone by the game's skeleton body colours (LPC's beige, gray and green sheets). */
export const BONES: Record<string, Material> = {
    white: BONE,
    beige: BONE,
    gray: material("#f0f0f8", "#c9c9e0", "#a3abb9", "#727080", "#4d4b5c", "#1d131e"),
    // LPC's green skeleton is a vivid, toxic green, not a mossy bone.
    green: material("#a8ff7a", "#38e83c", "#2f9a1d", "#2a6a22", "#1f4a1b", "#1d131e"),
}

/** Cloth and leather dyes an Ember humanoid's outfit may name. */
export const DYES: Record<string, string> = {
    red: "#a8383b",
    maroon: "#6e2a31",
    orange: "#c06a2c",
    ochre: "#b8873a",
    yellow: "#d6b546",
    green: "#55813f",
    forest: "#35583a",
    teal: "#2f7a7a",
    blue: "#3e63a0",
    navy: "#2b3a63",
    purple: "#674a8f",
    white: "#d9d4c7",
    gray: "#7b7c80",
    charcoal: "#45454c",
    black: "#2d2a30",
    brown: "#7a5134",
    tan: "#a98557",
    leather: "#6b4a30",
}

export const HAIR: Record<string, string> = {
    black: "#2b2629",
    brown: "#5d3b25",
    chestnut: "#7d4426",
    blonde: "#d1a654",
    red: "#a5452a",
    gray: "#8d8a8a",
    white: "#d8d4cf",
    // The rest of the game's (LPC's) hair colours.
    lightbrown: "#8a5f3a",
    gold: "#c9952f",
    orange: "#c0642a",
    green: "#3f7a3a",
    blue: "#3a5a9a",
    purple: "#6a4a8f",
}

export const dye = (name: string): Material => rampFrom(DYES[name] ?? DYES.gray)
export const hairDye = (name: string): Material => rampFrom(HAIR[name] ?? HAIR.brown)
